import { TabCountBadge } from '@/components/common/TabCountBadge'
import type { OverflowTab } from '@/components/page-context/types'
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { cn } from '@/lib/utils'
import { ChevronRight } from 'lucide-react'
import type React from 'react'
import { useState } from 'react'

const MIN_TABS_FOR_DROPDOWN = 3

interface OverflowTabsDropdownProps {
	tabs: OverflowTab[]
	onTabClick?: (tab: OverflowTab) => void
	dropdownLabel?: string
	dropdownCount?: number | null
	dropdownRef?: React.Ref<HTMLButtonElement>
	totalTabsCount?: number
	contentClassName?: string
}

export function OverflowTabsDropdown({
	tabs,
	onTabClick,
	dropdownLabel = `${tabs.length} more`,
	dropdownCount = null,
	dropdownRef,
	totalTabsCount,
	contentClassName,
}: OverflowTabsDropdownProps) {
	const [isOpen, setIsOpen] = useState(false)

	if (totalTabsCount === undefined || totalTabsCount <= MIN_TABS_FOR_DROPDOWN) {
		return null
	}

	return (
		<DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
			{/* A button, not a span: Radix passes its menu props to whatever it is given but adds no tabindex,
			    so on a span the tabs parked in here could not be reached by keyboard at all. */}
			<DropdownMenuTrigger asChild>
				<button
					type='button'
					ref={dropdownRef}
					aria-label={`${dropdownLabel}, ${totalTabsCount ?? tabs.length} tabs`}
					className={cn(
						'h-control border-dashed flex gap-1.5 items-center px-3 rounded-[6px] cursor-pointer select-none hover:text-foreground transition-colors',
						dropdownCount !== null
							? 'bg-header-tab-active-bg border-none text-header-tab-active-text'
							: 'bg-transparent text-header-tab-inactive-text',
					)}
					data-testid='overflow-tabs-dropdown-trigger'>
					<span
						className='truncate max-w-32 sm:max-w-48 md:max-w-64 text-body'
						title={dropdownLabel}
						data-testid='overflow-tabs-dropdown-label'>
						{dropdownLabel}
					</span>
					<TabCountBadge
						count={dropdownCount}
						active={true}
						showCount={dropdownCount !== null}
						className='ml-0'
						data-testid='overflow-tabs-dropdown-count'
					/>
					<ChevronRight className='size-(--icon-standalone) ml-1.5' />
				</button>
			</DropdownMenuTrigger>
			<DropdownMenuContent
				align='start'
				className={cn('w-max max-h-[300px] overflow-y-auto', contentClassName)}
				data-testid='overflow-tabs-dropdown-content'>
				{tabs.map(tab => (
					<button
						key={tab.key}
						type='button'
						className={cn(
							'group flex w-full items-center gap-1.5 px-2 h-(--height-control) rounded-[6px] cursor-pointer text-left text-body!',
							tab.isActive
								? 'bg-header-tab-active-bg text-header-tab-active-text'
								: 'bg-header-tab-inactive-bg text-header-tab-inactive-text group-hover:text-header-tab-active-text',
						)}
						aria-current={tab.isActive ? 'page' : undefined}
						data-testid={`overflow-tabs-dropdown-item-${tab.key}`}
						onClick={() => {
							onTabClick?.(tab)
							setIsOpen(false)
						}}>
						<span className='truncate'>{tab.name}</span>
						<TabCountBadge
							count={tab.count}
							loading={tab.countLoading}
							active={tab.isActive}
							showCount={tab.showCount !== false}
							tooltipSide='right'
						/>
					</button>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	)
}
